import type { LucideIcon } from 'lucide-react'

/**
 * One header action (EP-106 Item 1). Callers translate `label` themselves —
 * the component never calls `t()` on action copy. Provide exactly ONE of
 * `onClick` | `to` | `href`:
 *   - `onClick` → a plain <button> (opens a modal, fires a mutation, …)
 *   - `to`      → an internal react-router <Link>
 *   - `href`    → a raw <a> (`download` for blobs, `external` for new-tab)
 */
type PageActionCommon = {
  key: string
  label: string
  icon?: LucideIcon
  /** Red + auto-sorted LAST in the overflow (above a divider in the sheet). */
  destructive?: boolean
  /** Spinner + disabled (in-flight mutation). */
  loading?: boolean
  /** Disabled, no spinner (e.g. export when rows=0). */
  disabled?: boolean
  /** Per-action role/flag/state gate; dropped before layout. */
  hidden?: boolean
  /** Native `title` tooltip — contextual hint (e.g. "no payment methods yet"). */
  title?: string
  /** Defaults to `header-action-${key}`. */
  testId?: string
}

export type PageAction = PageActionCommon &
  (
    | { onClick: () => void; to?: never; href?: never; download?: never; external?: never }
    | { to: string; onClick?: never; href?: never; download?: never; external?: never }
    | { href: string; download?: boolean; external?: boolean; onClick?: never; to?: never }
  )

/** Falsy entries allowed so callers can inline-gate: `[a, cond && b, c]`. */
export type OverflowActions = ReadonlyArray<PageAction | false | null | undefined>

export interface PageHeaderActionsProps {
  /**
   * The one SOLID button. Optional: if omitted (a state with no natural
   * primary — e.g. a paid invoice), the first visible overflow action is
   * promoted to solid, so mobile is ALWAYS `[solid] [⋯]`.
   */
  primaryAction?: PageAction
  /** Everything else. Desktop: inline outline buttons. Mobile: one "⋯" sheet. */
  overflowActions?: OverflowActions
  /**
   * By default a SINGLE overflow item renders directly beside the primary on
   * mobile (no Options sheet). Set true to always collapse the overflow into the
   * "Options" (ตัวเลือก) sheet even for one item — use when the item's label is
   * long and would crowd the primary (e.g. Bank Reconciliation).
   */
  collapseOverflow?: boolean
}

export interface ResolvedHeaderActions {
  /** The single solid button, or null when nothing is visible. */
  solid: PageAction | null
  /** Remaining actions for the overflow, destructive entries pushed last. */
  menu: PageAction[]
}

/**
 * Pure layout resolver — the invariant that guarantees mobile is always
 * `[ one solid ] [ ⋯ ]`:
 *   1. drop hidden entries
 *   2. solid = visible primary, else the first visible overflow (promotion)
 *   3. menu = the rest, with destructive actions sorted last (stable)
 */
export function resolveHeaderActions(
  primaryAction?: PageAction,
  overflowActions?: OverflowActions,
): ResolvedHeaderActions {
  const overflow = (overflowActions ?? []).filter((a): a is PageAction => {
    if (!a) return false
    return !a.hidden
  })
  const primaryVisible = primaryAction && !primaryAction.hidden ? primaryAction : undefined
  const solid = primaryVisible ?? overflow[0] ?? null
  // When no primary, overflow[0] was promoted to solid — exclude it from the menu.
  const rest = primaryVisible ? overflow : overflow.slice(1)
  // Array.prototype.sort is stable (ES2019+): non-destructive keep their order,
  // destructive keep theirs, and all destructive land after all non-destructive.
  const menu = [...rest].sort(
    (a, b) => Number(Boolean(a.destructive)) - Number(Boolean(b.destructive)),
  )
  return { solid, menu }
}
